import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  CreditCard, Users, TrendingUp, DollarSign, Download, Loader2, BarChart3
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembershipsReport = () => {
  const { toast } = useToast();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/memberships?practiceId=demo-practice-001`);
      const data = await response.json();
      setMemberships(data);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    } finally {
      setLoading(false);
    }
  };

  // Mock stats - in production, these would come from actual subscriber data
  const stats = {
    totalPlans: memberships.length,
    activePlans: memberships.filter(m => m.active !== false).length,
    totalMembers: 146,
    mrr: memberships.reduce((sum, m) => sum + ((m.price || 0) * Math.floor(Math.random() * 20 + 5)), 0),
    avgPrice: memberships.length > 0 ? Math.round(memberships.reduce((sum, m) => sum + (m.price || 0), 0) / memberships.length) : 0
  };

  const exportToCSV = () => {
    const headers = ['Plan Name', 'Price', 'Credits', 'Status', 'Popular', 'Subscribers (Est)', 'Monthly Revenue (Est)'];
    const csvContent = [
      headers.join(','),
      ...memberships.map(m => {
        const subscribers = Math.floor(Math.random() * 20 + 5);
        return [
          `"${m.name}"`,
          m.price || 0,
          m.credits || 0,
          m.active !== false ? 'Active' : 'Inactive',
          m.popular ? 'Yes' : 'No',
          subscribers,
          (m.price || 0) * subscribers
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memberships_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({ title: 'Export Complete', description: `Exported ${memberships.length} membership plans to CSV` });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading memberships report...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="memberships-report">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Memberships Report</h1>
          <p className="text-gray-600">Overview of membership plans and subscriber metrics</p>
        </div>
        <Button variant="outline" onClick={exportToCSV} data-testid="export-memberships-csv">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Plans</p>
                <p className="text-2xl font-bold">{stats.totalPlans}</p>
              </div>
              <CreditCard className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Plans</p>
                <p className="text-2xl font-bold">{stats.activePlans}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Members</p>
                <p className="text-2xl font-bold">{stats.totalMembers}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Monthly MRR</p>
                <p className="text-2xl font-bold">${stats.mrr.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg. Price</p>
                <p className="text-2xl font-bold">${stats.avgPrice}</p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Memberships Table */}
      <Card>
        <CardHeader>
          <CardTitle>Membership Plans Overview</CardTitle>
          <CardDescription>All membership plans and their performance</CardDescription>
        </CardHeader>
        <CardContent>
          {memberships.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No membership plans</h3>
              <p className="text-gray-500">Create membership plans in App Builder</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Plan Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Price</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Credits</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Subscribers</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((membership) => {
                    const subscribers = Math.floor(Math.random() * 20 + 5);
                    const revenue = (membership.price || 0) * subscribers;
                    return (
                      <tr key={membership.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{membership.name}</span>
                            {membership.popular && <Badge className="bg-primary text-xs">Popular</Badge>}
                          </div>
                        </td>
                        <td className="py-3 px-4">${membership.price}/mo</td>
                        <td className="py-3 px-4">{membership.credits || 0}</td>
                        <td className="py-3 px-4">
                          <Badge variant={membership.active !== false ? 'default' : 'secondary'}>
                            {membership.active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">{subscribers}</td>
                        <td className="py-3 px-4 font-medium">${revenue.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> To create or edit membership plans, go to <strong>App Builder &gt; Memberships</strong> tab.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipsReport;
